import '@modules/workspaces/host'

import { WorkspaceService } from '@graft/nuget-gc.webportals.workspaces'
import type { StateCreator } from 'zustand'

import { getSessionToken } from '@common/helpers'

import { getWorkspacesByUserId } from '@modules/workspaces/workspaces'

import { mapWorkspaceToOrganization } from '../mappers/mapWorkspaceToOrganization'

import { deleteOrganizationAndReloadClientData, leaveOrganizationAndReloadClientData } from './organizations.thunks'
import type { OrganizationsStore } from './organizations.type'

type OrganizationsSet = Parameters<
  StateCreator<OrganizationsStore, [['zustand/immer', never]], [], OrganizationsStore>
>[0]

export const createOrganizationsActions = (
  set: OrganizationsSet
): Omit<OrganizationsStore, 'organizations' | 'loading' | 'error'> => ({
  getOrganizations: async () => {
    set((state: OrganizationsStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const workspaces = await getWorkspacesByUserId()
      set((state: OrganizationsStore) => {
        state.organizations = workspaces.map(mapWorkspaceToOrganization)
      })
    } catch {
      set((state: OrganizationsStore) => {
        state.organizations = []
      })
    } finally {
      set((state: OrganizationsStore) => {
        state.loading = false
      })
    }
  },
  createOrganization: async (name: string) => {
    set((state: OrganizationsStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const token = await getSessionToken()
      const organizationId = await WorkspaceService.addWorkspace(token, name)

      const workspaces = await getWorkspacesByUserId()

      set((state: OrganizationsStore) => {
        state.organizations = workspaces.map(mapWorkspaceToOrganization)
      })

      return organizationId ?? null
    } catch (err) {
      set((state: OrganizationsStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to create organization'
      })
      return null
    } finally {
      set((state: OrganizationsStore) => {
        state.loading = false
      })
    }
  },
  updateOrganizationName: async (organizationId: string, name: string) => {
    set((state: OrganizationsStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const token = await getSessionToken()
      await WorkspaceService.updateWorkspaceName(token, organizationId, name)
      set((state: OrganizationsStore) => {
        state.organizations = state.organizations.map((o) => (o.id === organizationId ? { ...o, name } : o))
      })
    } catch (err) {
      set((state: OrganizationsStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to update organization'
      })
    } finally {
      set((state: OrganizationsStore) => {
        state.loading = false
      })
    }
  },
  leaveOrganization: async (organizationId: string) => {
    set((state: OrganizationsStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const organizations = await leaveOrganizationAndReloadClientData(organizationId)
      set((state: OrganizationsStore) => {
        state.organizations = organizations
      })
    } catch (err) {
      set((state: OrganizationsStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to leave organization'
      })
    } finally {
      set((state: OrganizationsStore) => {
        state.loading = false
      })
    }
  },
  deleteOrganization: async (organizationId: string) => {
    set((state: OrganizationsStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const organizations = await deleteOrganizationAndReloadClientData(organizationId)
      set((state: OrganizationsStore) => {
        state.organizations = organizations
      })
    } catch (err) {
      set((state: OrganizationsStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to delete organization'
      })
    } finally {
      set((state: OrganizationsStore) => {
        state.loading = false
      })
    }
  },
  clearError: () => {
    set((state: OrganizationsStore) => {
      state.error = null
    })
  },
})
